import React, { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface IndustryStandard {
  id: string
  name: string
  category: string
  industry: string
  metrics: {
    average: number
    top_quartile: number
    median: number
    bottom_quartile: number
  }
  unit: string
  source: string
}

interface Comparison {
  metric: string
  your_value: number
  industry_average: number
  percentile: number
  performance: string
  recommendation: string
  unit: string
}

interface BenchmarkGroup {
  id: string
  name: string
  description?: string
  metrics: string[]
  members: string[]
}

interface TrendData {
  period: string
  value: number
}

const BenchmarkPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'industry' | 'period' | 'peers' | 'trends'>('industry')
  const [standards, setStandards] = useState<IndustryStandard[]>([])
  const [groups, setGroups] = useState<BenchmarkGroup[]>([])
  const [comparison, setComparison] = useState<any>(null)
  const [periodComparison, setPeriodComparison] = useState<any>(null)
  const [peerComparison, setPeerComparison] = useState<any>(null)
  const [trendData, setTrendData] = useState<any>(null)
  const [statistics, setStatistics] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedMetric, setSelectedMetric] = useState<string>('conversion_rate')
  const [selectedGroup, setSelectedGroup] = useState<string>('')

  // Form state for industry comparison
  const [userMetrics, setUserMetrics] = useState({
    conversion_rate: 3.2,
    cart_abandonment_rate: 65.0,
    return_rate: 12.5,
    cac: 35.0,
    clv: 280.0,
    repeat_purchase_rate: 32.0
  })

  // Form state for period comparison
  const [currentPeriod, setCurrentPeriod] = useState({
    revenue: 125000,
    orders: 1850,
    conversion_rate: 3.2,
    avg_order_value: 67.57
  })

  const [previousPeriod, setPreviousPeriod] = useState({
    revenue: 98000,
    orders: 1520,
    conversion_rate: 2.8,
    avg_order_value: 64.47
  })

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const [standardsRes, groupsRes, statsRes] = await Promise.all([
        fetch('/api/benchmarks/standards'),
        fetch('/api/benchmarks/groups'),
        fetch('/api/benchmarks/statistics')
      ])

      if (standardsRes.ok) {
        const data = await standardsRes.json()
        setStandards(data.standards || [])
      }
      if (groupsRes.ok) {
        const data = await groupsRes.json()
        setGroups(data.groups || [])
        if (data.groups?.length > 0) {
          setSelectedGroup(data.groups[0].id)
        }
      }
      if (statsRes.ok) {
        const data = await statsRes.json()
        setStatistics(data)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    }
    setLoading(false)
  }

  const runIndustryComparison = async () => {
    try {
      const res = await fetch('/api/benchmarks/compare/industry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ metrics: userMetrics })
      })
      if (res.ok) {
        const data = await res.json()
        setComparison(data)
      }
    } catch (error) {
      console.error('Error running comparison:', error)
    }
  }

  const runPeriodComparison = async () => {
    try {
      const res = await fetch('/api/benchmarks/compare/periods', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_period: currentPeriod,
          previous_period: previousPeriod,
          period_names: { current: 'This Quarter', previous: 'Last Quarter' }
        })
      })
      if (res.ok) {
        const data = await res.json()
        setPeriodComparison(data)
      }
    } catch (error) {
      console.error('Error running period comparison:', error)
    }
  }

  const runPeerComparison = async () => {
    if (!selectedGroup) return
    try {
      const res = await fetch(`/api/benchmarks/groups/${selectedGroup}/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ your_metrics: userMetrics })
      })
      if (res.ok) {
        const data = await res.json()
        setPeerComparison(data)
      }
    } catch (error) {
      console.error('Error running peer comparison:', error)
    }
  }

  const fetchTrendData = async () => {
    try {
      const res = await fetch(`/api/benchmarks/trends/${selectedMetric}?periods=12&interval=month`)
      if (res.ok) {
        const data = await res.json()
        setTrendData(data)
      }
    } catch (error) {
      console.error('Error fetching trend:', error)
    }
  }

  useEffect(() => {
    if (activeTab === 'trends') {
      fetchTrendData()
    }
  }, [activeTab, selectedMetric])

  const getPerformanceColor = (performance: string) => {
    switch (performance) {
      case 'excellent': return '#28a745'
      case 'good': return '#4A90E2'
      case 'average': return '#ffc107'
      case 'needs_improvement': return '#dc3545'
      default: return '#6c757d'
    }
  }

  const getPercentileColor = (percentile: number) => {
    if (percentile >= 75) return '#28a745'
    if (percentile >= 50) return '#4A90E2'
    if (percentile >= 25) return '#ffc107'
    return '#dc3545'
  }

  const filteredStandards = selectedCategory === 'all'
    ? standards
    : standards.filter(s => s.category === selectedCategory)

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      <Header />
      <main style={{ padding: '32px', maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a2e', marginBottom: '8px' }}>
            Benchmark Analytics
          </h1>
          <p style={{ color: '#64748b' }}>
            Compare your performance against industry standards, historical data, and peers
          </p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Industry Standards</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#4A90E2' }}>{statistics.industry_standards_count}</div>
            </div>
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Saved Benchmarks</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#28a745' }}>{statistics.total_benchmarks}</div>
            </div>
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Peer Groups</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#7B68EE' }}>{statistics.total_groups}</div>
            </div>
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>Categories</div>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#fd7e14' }}>{statistics.categories?.length || 0}</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px' }}>
          {[
            { id: 'industry', label: 'Industry Comparison' },
            { id: 'period', label: 'Period Comparison' },
            { id: 'peers', label: 'Peer Benchmarking' },
            { id: 'trends', label: 'Historical Trends' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                padding: '10px 20px',
                border: 'none',
                background: activeTab === tab.id ? '#4A90E2' : 'transparent',
                color: activeTab === tab.id ? 'white' : '#64748b',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Loading...</div>
        ) : (
          <>
            {/* Industry Comparison Tab */}
            {activeTab === 'industry' && (
              <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '24px' }}>
                {/* Input Panel */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: 'fit-content' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Your Metrics</h3>
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {Object.entries(userMetrics).map(([key, value]) => (
                      <div key={key}>
                        <label style={{ display: 'block', fontSize: '13px', color: '#64748b', marginBottom: '4px', textTransform: 'capitalize' }}>
                          {key.replace(/_/g, ' ')}
                        </label>
                        <input
                          type="number"
                          value={value}
                          onChange={e => setUserMetrics({ ...userMetrics, [key]: parseFloat(e.target.value) || 0 })}
                          step="0.1"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            fontSize: '14px'
                          }}
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={runIndustryComparison}
                    style={{
                      width: '100%',
                      marginTop: '16px',
                      padding: '12px',
                      background: '#4A90E2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Compare to Industry
                  </button>
                </div>

                {/* Results Panel */}
                <div>
                  {comparison ? (
                    <div>
                      {/* Overall Score */}
                      <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        marginBottom: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Overall Percentile</div>
                        <div style={{
                          fontSize: '48px',
                          fontWeight: 700,
                          color: getPercentileColor(comparison.overall_percentile)
                        }}>
                          {comparison.overall_percentile}%
                        </div>
                        <div style={{
                          display: 'inline-block',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '14px',
                          background: getPerformanceColor(comparison.overall_performance) + '20',
                          color: getPerformanceColor(comparison.overall_performance),
                          fontWeight: 500,
                          textTransform: 'capitalize',
                          marginTop: '8px'
                        }}>
                          {comparison.overall_performance.replace('_', ' ')}
                        </div>
                      </div>

                      {/* Individual Metrics */}
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {comparison.comparisons.map((c: Comparison, index: number) => (
                          <div
                            key={index}
                            style={{
                              background: 'white',
                              borderRadius: '12px',
                              padding: '16px 20px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div>
                                <div style={{ fontWeight: 600, textTransform: 'capitalize', marginBottom: '4px' }}>
                                  {c.metric.replace(/_/g, ' ')}
                                </div>
                                <div style={{ fontSize: '13px', color: '#64748b' }}>
                                  Your value: <strong>{c.your_value}{c.unit === 'percentage' ? '%' : c.unit === 'currency' ? ' GBP' : ''}</strong>
                                  {' | '}Industry avg: {c.industry_average}{c.unit === 'percentage' ? '%' : c.unit === 'currency' ? ' GBP' : ''}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{
                                  fontSize: '20px',
                                  fontWeight: 700,
                                  color: getPercentileColor(c.percentile)
                                }}>
                                  {c.percentile}%
                                </div>
                                <span style={{
                                  fontSize: '12px',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  background: getPerformanceColor(c.performance) + '20',
                                  color: getPerformanceColor(c.performance),
                                  textTransform: 'capitalize'
                                }}>
                                  {c.performance.replace('_', ' ')}
                                </span>
                              </div>
                            </div>
                            {/* Percentile Bar */}
                            <div style={{ background: '#e2e8f0', borderRadius: '4px', height: '8px', marginBottom: '8px' }}>
                              <div style={{
                                width: `${c.percentile}%`,
                                height: '100%',
                                background: getPercentileColor(c.percentile),
                                borderRadius: '4px',
                                transition: 'width 0.5s'
                              }} />
                            </div>
                            <div style={{ fontSize: '13px', color: '#64748b', fontStyle: 'italic' }}>
                              {c.recommendation}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div>
                      {/* Industry Standards Grid */}
                      <div style={{ marginBottom: '16px' }}>
                        <select
                          value={selectedCategory}
                          onChange={e => setSelectedCategory(e.target.value)}
                          style={{ padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                        >
                          <option value="all">All Categories</option>
                          <option value="conversion">Conversion</option>
                          <option value="marketing">Marketing</option>
                          <option value="revenue">Revenue</option>
                          <option value="operations">Operations</option>
                          <option value="retention">Retention</option>
                        </select>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                        {filteredStandards.map(standard => (
                          <div
                            key={standard.id}
                            style={{
                              background: 'white',
                              borderRadius: '12px',
                              padding: '20px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                          >
                            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px' }}>{standard.name}</div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>
                              <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '4px' }}>{standard.category}</span>
                              {' '}<span style={{ fontStyle: 'italic' }}>Source: {standard.source}</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px' }}>
                              <div>
                                <div style={{ color: '#64748b' }}>Average</div>
                                <div style={{ fontWeight: 600 }}>{standard.metrics.average}{standard.unit === 'percentage' ? '%' : ''}</div>
                              </div>
                              <div>
                                <div style={{ color: '#64748b' }}>Median</div>
                                <div style={{ fontWeight: 600 }}>{standard.metrics.median}{standard.unit === 'percentage' ? '%' : ''}</div>
                              </div>
                              <div>
                                <div style={{ color: '#64748b' }}>Top 25%</div>
                                <div style={{ fontWeight: 600, color: '#28a745' }}>{standard.metrics.top_quartile}{standard.unit === 'percentage' ? '%' : ''}</div>
                              </div>
                              <div>
                                <div style={{ color: '#64748b' }}>Bottom 25%</div>
                                <div style={{ fontWeight: 600, color: '#dc3545' }}>{standard.metrics.bottom_quartile}{standard.unit === 'percentage' ? '%' : ''}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Period Comparison Tab */}
            {activeTab === 'period' && (
              <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '24px' }}>
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: 'fit-content' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Period Comparison</h3>

                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: '#4A90E2' }}>Current Period</h4>
                    {Object.entries(currentPeriod).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '2px', textTransform: 'capitalize' }}>
                          {key.replace(/_/g, ' ')}
                        </label>
                        <input
                          type="number"
                          value={value}
                          onChange={e => setCurrentPeriod({ ...currentPeriod, [key]: parseFloat(e.target.value) || 0 })}
                          step="0.01"
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                    ))}
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: '#64748b' }}>Previous Period</h4>
                    {Object.entries(previousPeriod).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '8px' }}>
                        <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '2px', textTransform: 'capitalize' }}>
                          {key.replace(/_/g, ' ')}
                        </label>
                        <input
                          type="number"
                          value={value}
                          onChange={e => setPreviousPeriod({ ...previousPeriod, [key]: parseFloat(e.target.value) || 0 })}
                          step="0.01"
                          style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={runPeriodComparison}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#4A90E2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Compare Periods
                  </button>
                </div>

                <div>
                  {periodComparison ? (
                    <div>
                      {/* Summary */}
                      <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        marginBottom: '16px'
                      }}>
                        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>Period Summary</h3>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', textAlign: 'center' }}>
                          <div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: '#28a745' }}>{periodComparison.summary.improved_metrics}</div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>Improved</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: '#dc3545' }}>{periodComparison.summary.declined_metrics}</div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>Declined</div>
                          </div>
                          <div>
                            <div style={{ fontSize: '24px', fontWeight: 700, color: '#64748b' }}>{periodComparison.summary.unchanged_metrics}</div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>Unchanged</div>
                          </div>
                          <div>
                            <div style={{
                              fontSize: '24px',
                              fontWeight: 700,
                              color: periodComparison.summary.overall_trend === 'improving' ? '#28a745' : periodComparison.summary.overall_trend === 'declining' ? '#dc3545' : '#64748b',
                              textTransform: 'capitalize'
                            }}>
                              {periodComparison.summary.overall_trend}
                            </div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>Overall Trend</div>
                          </div>
                        </div>
                      </div>

                      {/* Metric Comparisons */}
                      <div style={{ display: 'grid', gap: '12px' }}>
                        {periodComparison.comparisons.map((c: any, index: number) => (
                          <div
                            key={index}
                            style={{
                              background: 'white',
                              borderRadius: '12px',
                              padding: '16px 20px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <div>
                              <div style={{ fontWeight: 600, textTransform: 'capitalize', marginBottom: '4px' }}>
                                {c.metric.replace(/_/g, ' ')}
                              </div>
                              <div style={{ fontSize: '13px', color: '#64748b' }}>
                                {periodComparison.period_names.previous}: {c.previous_value.toLocaleString()}
                                {' → '}
                                {periodComparison.period_names.current}: {c.current_value.toLocaleString()}
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{
                                fontSize: '24px',
                                fontWeight: 700,
                                color: c.change_percentage > 0 ? '#28a745' : c.change_percentage < 0 ? '#dc3545' : '#64748b'
                              }}>
                                {c.change_percentage > 0 ? '+' : ''}{c.change_percentage.toFixed(1)}%
                              </div>
                              <div style={{ fontSize: '20px' }}>
                                {c.trend === 'up' ? '📈' : c.trend === 'down' ? '📉' : '➡️'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '60px', textAlign: 'center', color: '#64748b' }}>
                      Enter your metrics and click "Compare Periods" to see the comparison
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Peer Comparison Tab */}
            {activeTab === 'peers' && (
              <div style={{ display: 'grid', gridTemplateColumns: '350px 1fr', gap: '24px' }}>
                <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', height: 'fit-content' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>Peer Group Comparison</h3>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px' }}>Select Peer Group</label>
                    <select
                      value={selectedGroup}
                      onChange={e => setSelectedGroup(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                    >
                      {groups.map(group => (
                        <option key={group.id} value={group.id}>{group.name}</option>
                      ))}
                    </select>
                  </div>

                  {selectedGroup && groups.find(g => g.id === selectedGroup) && (
                    <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', color: '#64748b' }}>
                      <div><strong>Members:</strong> {groups.find(g => g.id === selectedGroup)?.members.length}</div>
                      <div><strong>Metrics:</strong> {groups.find(g => g.id === selectedGroup)?.metrics.join(', ')}</div>
                    </div>
                  )}

                  <button
                    onClick={runPeerComparison}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#7B68EE',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 500,
                      cursor: 'pointer'
                    }}
                  >
                    Compare to Peers
                  </button>
                </div>

                <div>
                  {peerComparison ? (
                    <div>
                      <div style={{
                        background: 'white',
                        borderRadius: '12px',
                        padding: '24px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        marginBottom: '16px',
                        textAlign: 'center'
                      }}>
                        <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>{peerComparison.group_name}</h3>
                        <div style={{ color: '#64748b' }}>Comparing against {peerComparison.peer_count} peers</div>
                      </div>

                      <div style={{ display: 'grid', gap: '12px' }}>
                        {peerComparison.comparisons.map((c: any, index: number) => (
                          <div
                            key={index}
                            style={{
                              background: 'white',
                              borderRadius: '12px',
                              padding: '20px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                              <div>
                                <div style={{ fontWeight: 600, textTransform: 'capitalize', marginBottom: '4px' }}>
                                  {c.metric.replace(/_/g, ' ')}
                                </div>
                                <div style={{ fontSize: '13px', color: '#64748b' }}>
                                  Your value: <strong>{c.your_value.toFixed(2)}</strong>
                                  {' | '}Peer avg: {c.peer_average.toFixed(2)}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '24px', fontWeight: 700 }}>
                                  #{c.your_rank}
                                </div>
                                <div style={{ fontSize: '13px', color: '#64748b' }}>of {c.total_peers}</div>
                              </div>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', fontSize: '13px', color: '#64748b' }}>
                              <div>
                                <div>Min: {c.peer_min.toFixed(2)}</div>
                              </div>
                              <div style={{ textAlign: 'center' }}>
                                <div>Avg: {c.peer_average.toFixed(2)}</div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <div>Max: {c.peer_max.toFixed(2)}</div>
                              </div>
                            </div>
                            <div style={{ background: '#e2e8f0', borderRadius: '4px', height: '8px', marginTop: '12px' }}>
                              <div style={{
                                width: `${c.percentile}%`,
                                height: '100%',
                                background: getPercentileColor(c.percentile),
                                borderRadius: '4px'
                              }} />
                            </div>
                            <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px', textAlign: 'right' }}>
                              {c.percentile}th percentile
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: 'white', borderRadius: '12px', padding: '60px', textAlign: 'center', color: '#64748b' }}>
                      Select a peer group and click "Compare to Peers" to see how you rank
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Historical Trends Tab */}
            {activeTab === 'trends' && (
              <div>
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', alignItems: 'center' }}>
                  <select
                    value={selectedMetric}
                    onChange={e => setSelectedMetric(e.target.value)}
                    style={{ padding: '10px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px' }}
                  >
                    <option value="conversion_rate">Conversion Rate</option>
                    <option value="revenue">Revenue</option>
                    <option value="orders">Orders</option>
                    <option value="avg_order_value">Average Order Value</option>
                    <option value="return_rate">Return Rate</option>
                  </select>
                  <button
                    onClick={fetchTrendData}
                    style={{
                      padding: '10px 20px',
                      background: '#4A90E2',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Refresh
                  </button>
                </div>

                {trendData && (
                  <div>
                    {/* Stats Summary */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginBottom: '24px' }}>
                      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Minimum</div>
                        <div style={{ fontSize: '20px', fontWeight: 700 }}>{trendData.statistics.min}</div>
                      </div>
                      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Average</div>
                        <div style={{ fontSize: '20px', fontWeight: 700 }}>{trendData.statistics.average}</div>
                      </div>
                      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Maximum</div>
                        <div style={{ fontSize: '20px', fontWeight: 700 }}>{trendData.statistics.max}</div>
                      </div>
                      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Change</div>
                        <div style={{
                          fontSize: '20px',
                          fontWeight: 700,
                          color: trendData.statistics.change_from_start > 0 ? '#28a745' : trendData.statistics.change_from_start < 0 ? '#dc3545' : '#64748b'
                        }}>
                          {trendData.statistics.change_from_start > 0 ? '+' : ''}{trendData.statistics.change_from_start}%
                        </div>
                      </div>
                      <div style={{ background: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' }}>
                        <div style={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Trend</div>
                        <div style={{
                          fontSize: '20px',
                          fontWeight: 700,
                          textTransform: 'capitalize',
                          color: trendData.statistics.trend_direction === 'up' ? '#28a745' : trendData.statistics.trend_direction === 'down' ? '#dc3545' : '#64748b'
                        }}>
                          {trendData.statistics.trend_direction === 'up' ? '📈' : trendData.statistics.trend_direction === 'down' ? '📉' : '➡️'} {trendData.statistics.trend_direction}
                        </div>
                      </div>
                    </div>

                    {/* Simple Bar Chart */}
                    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '20px', textTransform: 'capitalize' }}>
                        {selectedMetric.replace(/_/g, ' ')} Trend
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: '200px' }}>
                        {trendData.data.map((d: TrendData, index: number) => {
                          const maxValue = Math.max(...trendData.data.map((x: TrendData) => x.value))
                          const height = (d.value / maxValue) * 180
                          return (
                            <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>
                                {d.value.toFixed(0)}
                              </div>
                              <div
                                style={{
                                  width: '100%',
                                  height: `${height}px`,
                                  background: index === trendData.data.length - 1 ? '#4A90E2' : '#94a3b8',
                                  borderRadius: '4px 4px 0 0'
                                }}
                              />
                              <div style={{ fontSize: '10px', color: '#64748b', marginTop: '8px', writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: '60px' }}>
                                {d.period}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}

export default BenchmarkPage
